import { z } from "zod/v3";

const pageSchema = z
  .object({
    id: z
      .string()
      .describe(
        "Stable PrimeUI page identifier. Use this to match the same page across tool responses.",
      ),
    title: z
      .string()
      .describe(
        "Human-readable page title. Show this to the user when confirming which pages to import.",
      ),
    slug: z
      .string()
      .describe(
        "PrimeUI route slug, for example '/', '/pricing', '/docs/getting-started'. Use to match user intent.",
      ),
    pageType: z
      .string()
      .describe(
        "PrimeUI page type classification (for example landing, docs, pricing, blogIndex, legal).",
      ),
    isReadyToExport: z
      .boolean()
      .describe(
        "True when this page has an active export-ready variant. Only ready pages are expected in export output.",
      ),
    pagePath: z
      .string()
      .describe(
        "Relative file path to the page source inside downloaded export root. Copy page code from this exact path.",
      ),
    componentsPath: z
      .string()
      .describe(
        "Relative directory path inside downloaded export root where page-level components live. Use as dependency copy start.",
      ),
  })
  .describe(
    "PrimeUI page descriptor used by project and export tools. Paths are always relative to export project root.",
  );

const exportStatusSchema = z
  .enum(["in_progress", "completed", "failed"])
  .describe(
    "Export lifecycle state. Use 'completed' before calling download_export.",
  );

const exportItemSchema = z
  .object({
    id: z
      .string()
      .describe(
        "Export identifier. Pass this value to download_export input.id.",
      ),
    status: exportStatusSchema,
    createdAt: z
      .string()
      .describe("Export creation timestamp in ISO-8601 UTC format."),
  })
  .describe("Single export record from PrimeUI export history.");

const exportSummarySchema = z.object({
  total: z.number().describe("Total pages processed in this export run."),
  successful: z.number().describe("Number of pages exported successfully."),
  failed: z.number().describe("Number of pages that failed export."),
});

const exportManifestSchema = z.object({
  success: z
    .boolean()
    .describe("True when export processing succeeded for this item."),
  message: z.string().describe("Export status message for this item."),
  files: z
    .array(z.string())
    .describe("Authoritative exported file list for this item."),
});

const exportableSchema = z.object({
  key: z.string().describe("Shared exportable key from export manifest."),
  isReadyToExport: z
    .boolean()
    .describe("True when this shared exportable was eligible for export."),
  manifest: exportManifestSchema.describe(
    "Export manifest for this shared exportable, aligned with page manifest shape.",
  ),
});

const projectExportableSchema = z.object({
  key: z
    .string()
    .describe("Stable shared exportable key from PrimeUI project info."),
  description: z
    .string()
    .describe(
      "Agent-oriented summary of what this shared exportable provides.",
    ),
  isReadyToExport: z
    .boolean()
    .describe(
      "Best-effort readiness snapshot for whether this shared exportable can be included in export now.",
    ),
});

const exportPageManifestSchema = exportManifestSchema.extend({
  files: z
    .array(z.string())
    .describe(
      "Authoritative list of page-related files from PrimeUI export manifest, including shared project files required by this page.",
    ),
});

const exportPageSchema = z.object({
  id: z
    .string()
    .describe("Stable PrimeUI page identifier for this export entry."),
  title: z
    .string()
    .optional()
    .describe(
      "Optional page title from export manifest. Can be undefined for some legacy records.",
    ),
  slug: z.string().describe("PrimeUI page slug included in this export."),
  pageType: z
    .string()
    .describe("PrimeUI page type for routing/path resolution."),
  isReadyToExport: z
    .literal(true)
    .describe("Always true for pages included in export payload."),
  pagePath: z
    .string()
    .describe("Page source file path relative to export root."),
  componentsPath: z
    .string()
    .describe("Page components directory path relative to export root."),
  manifest: exportPageManifestSchema,
});

const componentExportComponentSchema = z.object({
  componentId: z
    .string()
    .describe("PrimeUI component registry identifier used to create export."),
  name: z
    .string()
    .describe("Human-readable PrimeUI component name from registry metadata."),
  pageType: z
    .string()
    .describe("Page type used by the virtual component export snapshot."),
  family: z
    .string()
    .describe("Component family/group metadata from the PrimeUI registry."),
  defaultPropsAvailable: z
    .boolean()
    .describe("True when registry default props were available for export."),
  isReadyToExport: z
    .boolean()
    .describe("True when this component export payload is ready to download."),
});

const componentExportPageSchema = exportPageSchema.omit({
  pagePath: true,
  componentsPath: true,
});

const dependencySectionSchema = z.enum([
  "dependencies",
  "devDependencies",
  "peerDependencies",
]);

const dependencyToAddSchema = z.object({
  packageName: z
    .string()
    .describe("Dependency package name that was missing in user package.json."),
  version: z.string().describe("Version from exported project's package.json."),
  section: dependencySectionSchema.describe(
    "package.json section where dependency should be added.",
  ),
});

const dependencyVersionConflictSchema = z.object({
  packageName: z
    .string()
    .describe("Dependency package name with version mismatch."),
  section: dependencySectionSchema.describe(
    "Section where export expects this dependency.",
  ),
  exportVersion: z
    .string()
    .describe("Version found in exported project's package.json."),
  userVersion: z
    .string()
    .describe("Version currently present in user's package.json."),
});

const conflictFileSchema = z.object({
  targetPath: z
    .string()
    .describe("Relative conflict target path inside user project root."),
  isBinary: z
    .boolean()
    .describe("True if conflict comparison was treated as binary data."),
});

const skippedCopyFileSchema = z.object({
  targetPath: z
    .string()
    .describe(
      "Relative target path intentionally not copied into user project root.",
    ),
  reason: z
    .enum([
      "package_json_managed_as_dependencies",
      "virtual_component_route",
      "not_component_dependency",
    ])
    .describe(
      "Machine-readable reason for skipping this file. package_json_managed_as_dependencies means package.json was handled as dependency guidance instead of direct file copy. virtual_component_route means the internal component-export route was intentionally not installed into the target app. not_component_dependency means the manifest file belongs to page/template context outside the selected registry component dependency set.",
    ),
});

const runtimeGuidanceArtifactSchema = z.object({
  kind: z
    .enum(["export_manifest", "reference_export", "conflict_report"])
    .describe(
      "Artifact kind. export_manifest is the local sidecar manifest and authoritative file-level contract. reference_export is the downloaded export project used as the golden example when behavior diverges or integration is unclear. conflict_report is the saved local diff report for conflicting files that represent required imported functionality.",
    ),
  path: z
    .string()
    .describe("Absolute local path to the generated or referenced artifact."),
  description: z
    .string()
    .describe("Why this artifact matters for the current tool result."),
});

const runtimeGuidanceSchema = z.object({
  artifacts: z
    .array(runtimeGuidanceArtifactSchema)
    .describe(
      "Concrete local artifacts produced or referenced by the tool result. Review these before continuing the import flow.",
    ),
  requiredActions: z
    .string()
    .describe(
      "Mandatory next-step instruction derived from the actual tool result. Follow this text before continuing the flow.",
    ),
});

const copyComponentSelectedBlockSchema = z.object({
  blockId: z.string().describe("Selected block instance identifier."),
  componentId: z
    .string()
    .describe("Canonical component file identity from block.key."),
  slot: z
    .string()
    .nullable()
    .describe("Optional slot value for the selected block instance."),
  props: z
    .record(z.unknown())
    .nullable()
    .describe("Exact props payload for the selected block instance."),
  occurrenceIndex: z
    .number()
    .describe(
      "1-based position of this block among components with the same componentId.",
    ),
  contentKey: z
    .string()
    .describe(
      "Content binding key for this selected instance. First instance uses componentId, later duplicates append -2, -3, and so on.",
    ),
});

const copyComponentGuidanceSchema = z.object({
  requiredActions: z
    .array(z.string())
    .describe("Concrete next actions for finishing component transfer."),
  notes: z
    .array(z.string())
    .describe("Important semantics and caveats for this transfer result."),
});

const registryComponentInsertionSchema = z.object({
  imports: z
    .array(z.string())
    .describe("Import statements needed for inserting the copied component."),
  propsCode: z
    .string()
    .describe(
      "Materialized props source derived from the downloaded virtual component export.",
    ),
  jsx: z
    .string()
    .describe("JSX snippet for rendering the materialized component instance."),
  referenceFiles: z
    .array(z.string())
    .describe(
      "Downloaded export files used as the source of truth for materialized insertion.",
    ),
});

const pageDetailsSchema = pageSchema.extend({
  pageInstruction: z
    .string()
    .nullable()
    .describe(
      "Current instruction text for the active page variant. Null when no active variant is set.",
    ),
});

const pageVariantSchema = z
  .object({
    id: z.string().describe("Active variant identifier."),
    name: z.string().describe("Active variant display name."),
  })
  .nullable()
  .describe(
    "Active page variant. Null when page has no active variant and components are unavailable.",
  );

const pageComponentSchema = z.object({
  blockId: z
    .string()
    .describe("Stable block identifier. Use as primary component instance ID."),
  componentId: z
    .string()
    .describe("Component key from variant blocks payload (block.key)."),
  componentGroup: z
    .string()
    .describe("Component family/group normalized from component key."),
  slot: z
    .string()
    .nullable()
    .describe("Optional slot value from block payload."),
  props: z
    .record(z.unknown())
    .nullable()
    .describe("Raw block props payload from PrimeUI."),
});

const inspectPageReportRowSchema = z.object({
  number: z.number().describe("1-based row number matching report order."),
  componentGroup: z.string().describe("Component family/group label."),
  componentId: z.string().describe("Component key (block key)."),
  blockId: z
    .string()
    .describe("Primary component instance identifier for future operations."),
  title: z
    .string()
    .nullable()
    .describe("Title extracted by MCP from component props."),
  description: z
    .string()
    .nullable()
    .describe("Description extracted by MCP from component props."),
});

const projectRootInputSchema = z
  .string()
  .optional()
  .describe(
    "Optional absolute project root path. Use when .primeui/project.json cannot be auto-resolved from current working directory.",
  );

const optionalProjectRootInputObjectSchema = z
  .object({
    projectRoot: projectRootInputSchema,
  })
  .describe("Optional tool input with project root override.");

// ============================================================
// Shared constants
// ============================================================

const TRIGGER_PHRASES = [
  "import page from PrimeUI",
  "add page from PrimeUI",
  "sync pages",
  "pull page",
  "transfer page",
  "get page from PrimeUI",
  "bring page from PrimeUI",
  "export from PrimeUI",
].join(", ");

const WORKFLOW_SUMMARY = `
WORKFLOW ORDER (always follow this sequence):
1. clear_temp -> cleanup stale temp files from previous runs before starting new import flow
2. get_project_info -> discover PrimeUI pages and shared exportables
3. Reconcile PrimeUI pages with local project pages/routes/paths, then confirm target pages with user
4. inspect_page -> inspect selected page components and produce a structured comparison table
5. create_export -> generate export snapshot and local manifest with page file lists + shared exportables
6. download_export -> download to temp directory
7. copy_page, copy_component, copy_exportable, or copy_registry_component -> transfer one selected page, one selected block-level page component, one selected shared exportable, or one registry component safely
8. Reconcile integration points, dependency follow-up, and reported conflicts
`.trim();

// ============================================================
// Server Instructions (initialInstructions)
// ============================================================

export const initialInstructions = `
PrimeUI MCP enables importing pages from PrimeUI Studio into your local project.

TRIGGER EXAMPLES (semantic intent, not exact phrase matching): ${TRIGGER_PHRASES}

${WORKFLOW_SUMMARY}

ADDITIONAL CAPABILITIES:
- PrimeUI MCP also exposes atomic external API tools for project description, project pages, page variants, active-variant selection, and issue reports.
- PrimeUI MCP also exposes component planning tools such as component_candidates_get and component_props_validate for exported-project page building.
- These additional tools are OPTIONAL capabilities for extended scenarios.
- They are NOT part of the primary import/export workflow above.
- Do not replace get_project_info/create_export/download_export/copy_* with the additional API tools when the user wants the standard page import/export flow.

CRITICAL RULES FOR PAGE IMPORT:
- Import is PAGE-BY-PAGE only. Never import the entire project at once.
- Always call clear_temp at the very beginning of a new import flow to avoid stale export state.
- After get_project_info, always compare PrimeUI pages against the user's existing local project pages before proposing imports.
- Reconciliation report MUST include: page presence match, slug/pagePath/componentsPath path match, and proposed action per page.
- Decision matrix:
  - PrimeUI + local page exists -> candidate for update/review.
  - PrimeUI exists, local missing -> import candidate.
  - Local exists, PrimeUI missing -> custom local page, no action unless user asks.
  - Both exist but path mismatch -> ambiguous, export + compare before deciding.
- For targeted component-level analysis on a specific page, call inspect_page before export/copy.
- After inspect_page returns report table, supplement that table with local project page state:
  mark what already exists, what is missing, and what differs.
- Before creating export, ask user which pages to add/update (specific pages or all missing pages) and keep that choice in thread context.
- create_export response includes:
  - page-level manifest files per page ('pages[].manifest.files'),
  - shared exportables list ('export.exportables').
  Mention both to user after create_export, because this is the source of truth for copy decisions.
- The downloaded export in .primeui/temp/ contains a full standalone Next.js project.
  Do NOT copy it wholesale.
- After download_export, choose the copy tool based on what the user wants to transfer:
  - copy_page for page manifests,
  - copy_component for one selected block-level component instance,
  - copy_exportable for shared exportables.
- Always call copy_page, copy_component, copy_exportable, or copy_registry_component instead of manual file copy.
- copy_page performs safe copy only:
  - new files are copied,
  - identical files are reported,
  - conflicting files are NEVER overwritten and are returned with diff.
- copy_page reads and validates files only from 'pages[].manifest.files' in local sidecar manifest.
- copy_page may add only missing dependencies to user's package.json.
  It never upgrades existing dependency versions and never runs dependency installation.
- copy_component uses inspect_page block data plus downloaded export artifacts.
  It copies only the canonical component file that can be resolved reliably for the selected componentId.
- copy_component returns exact selected block props, occurrenceIndex, and contentKey for the chosen blockId.
- copy_component treats manualInsertionTargets, potentialDependencyFiles, and potentialConflictFiles as review hints, not as mandatory full-page merge instructions.
- copy_exportable reads and validates files only from 'export.exportables[].manifest.files' in local sidecar manifest.
- copy_exportable returns status 'not_available' when the shared exportable is disabled, not configured, or currently has no copyable export files.
- copy_exportable never copies package.json automatically.
  Dependency follow-up is returned via 'missedDependencies' and 'dependenciesVersionConflicts' and must be handled explicitly by the agent or user.
- After page copy operations, inspect integration points (navigation configs, link menus, route registries, page indexes).
- If integration pattern is obvious, apply it and explicitly mark it in the report with "⚠ integration update".
- If integration pattern is unclear, ask the user before editing integration files.
`.trim();

// ============================================================
// Tool: get_project_info
// ============================================================

export const toolGetProjectInfo = {
  title: "PrimeUI Project Info",
  description: `ENTRY POINT for all PrimeUI import operations. Always start here. Returns project metadata plus a lightweight inventory of pages and shared exportables you can reason about before export.

WHEN TO USE: Call this FIRST when user wants to import, add, sync, pull, or transfer a page from PrimeUI. Intent examples (semantic intent, not exact phrase matching): ${TRIGGER_PHRASES}.

AFTER CALLING:
- Scan the user's local project to discover existing pages/routes and related page component folders.
- Build a reconciliation report between PrimeUI and local project with action labels:
  - Page exists in both PrimeUI and local project.
  - Page exists in PrimeUI but not in local project -> import candidate.
  - Page exists in local project but not in PrimeUI -> custom local page, no action by default.
  - Page exists in both but slug/pagePath/componentsPath mapping does not match -> ambiguous, requires export-based comparison before decision.
- If user already requested specific pages, first verify those pages exist in PrimeUI and are isReadyToExport: true.
- If user already requested specific shared exportables, verify those exportables exist in the lightweight exportables inventory and note their readiness before create_export.
- If any requested page is not found or not ready, report this immediately and ask for replacement page choices.
- Ask the user which pages or shared exportables to add or update now and keep this decision in thread context.
- Import works one page or one shared exportable at a time. Multiple transfers are allowed, but each item is handled explicitly.
- Only after user decision is clear, proceed to create_export.

${WORKFLOW_SUMMARY}`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: z.object({
    projectId: z
      .string()
      .describe(
        "PrimeUI project identifier associated with the API key and current import session context.",
      ),
    projectName: z
      .string()
      .describe(
        "Human-readable PrimeUI project name for confirmations in chat.",
      ),
    metadata: z
      .record(z.unknown())
      .describe(
        "Additional project metadata from PrimeUI. May include project-description and other context fields.",
      ),
    exportables: z
      .array(projectExportableSchema)
      .describe(
        "Lightweight inventory of shared exportables (for example logo, cookie banner, announcement banner, theme, image assets). Use description + isReadyToExport to plan imports before create_export.",
      ),
    pages: z
      .array(pageSchema)
      .describe(
        "All pages visible for this project context. Filter by user request and isReadyToExport before creating export.",
      ),
  }),
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: inspect_page
// ============================================================

export const toolInspectPage = {
  title: "PrimeUI Inspect Page",
  description: `Targeted inspection tool for one PrimeUI page. Returns page details, active variant (if present), raw components payload, and a formatted report table.

WHEN TO USE:
- Call this after get_project_info when user wants component-level analysis for a specific page.
- Use this before export/copy when user asks to compare or selectively update page sections.

AFTER CALLING:
- Use report + reportRows to present the component table:
  Number (1-based), ComponentGroup (ComponentId), Title/Description, blockId.
- Then supplement this table with the CURRENT local project page state:
  what already exists, what is missing, and what differs.
- Do not assume componentId is unique. Use blockId as the primary component instance identifier.

If page has no active variant, components is null (not empty array), and report explains why.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    pageSlug: z
      .string()
      .describe(
        "PrimeUI page slug to inspect, for example '/', '/pricing', '/docs/getting-started'.",
      ),
  },
  outputSchema: z.object({
    page: pageDetailsSchema.describe(
      "Single PrimeUI page details resolved by slug with pageInstruction.",
    ),
    variant: pageVariantSchema,
    components: z
      .array(pageComponentSchema)
      .nullable()
      .describe(
        "Raw component instances from active variant. Null when page has no active variant.",
      ),
    report: z
      .string()
      .describe(
        "Formatted report table generated by MCP from component payload (includes extracted title/description summary).",
      ),
    reportRows: z
      .array(inspectPageReportRowSchema)
      .describe(
        "Structured report rows with 1-based numbering and extracted title/description fields.",
      ),
  }),
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: list_exports
// ============================================================

export const toolListExports = {
  title: "PrimeUI Export List",
  description: `Auxiliary tool to list previously created exports with their IDs, statuses, and creation dates. This is NOT the starting point for page import - start with get_project_info instead.

WHEN TO USE: This is an OPTIONAL helper tool, NOT part of the main import flow. Use it only when:
- Something went wrong with an export and you need to check its status.
- The user explicitly references a previous export by date or context.
- You need to verify whether an export initiated via PrimeUI Studio UI has completed.

Note: exports can be created both through create_export AND by the user directly in PrimeUI Studio UI. This tool shows all of them.

Do NOT use this tool as a substitute for create_export. Always create a fresh export unless the user explicitly asks to use a previous one.

${WORKFLOW_SUMMARY}`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: z.object({
    exports: z
      .array(exportItemSchema)
      .describe(
        "Available exports sorted by API policy. Use item.id to download a specific prior export if user asks.",
      ),
  }),
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: create_export
// ============================================================

export const toolCreateExport = {
  title: "PrimeUI Create Export",
  description: `Create a new export snapshot from the current PrimeUI project state and wait for completion. Returns strict export manifest payload from API, including page-level file manifests and shared exportables.

WHEN TO USE: Call this AFTER the user has confirmed which pages they want to import via get_project_info. This tool is REQUIRED before download_export, unless the user explicitly and directly asked to download a specific previous export.

AFTER CALLING:
- Verify that all user-selected pages are present in the export result AND have isReadyToExport: true.
- Verify that any user-selected shared exportables are present in export.exportables.
- If path mismatch pages were flagged during project-info reconciliation, ensure they are included for deeper comparison after download.
- If any required page is missing or not ready, report and pause for user decision before proceeding.
- Mention that pages can later be transferred via copy_page or copy_component, and shared exportables can be transferred separately via copy_exportable.
- Local sidecar manifest is saved to .primeui/temp/exports/[exportId].manifest.json from this API payload.
- Use the returned export ID to call download_export.
- If guidance is present in the result, read guidance.artifacts and follow guidance.requiredActions before continuing.

${WORKFLOW_SUMMARY}`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: z.object({
    export: z
      .object({
        id: z
          .string()
          .describe(
            "Freshly created export identifier. Use this as download_export input.id.",
          ),
        status: exportStatusSchema,
        createdAt: z
          .string()
          .describe("Export creation timestamp in ISO-8601 UTC format."),
        expiresAt: z
          .string()
          .nullable()
          .describe(
            "Export expiration timestamp in ISO-8601 UTC format, or null when export has no explicit expiration.",
          ),
        summary: exportSummarySchema.describe(
          "Export run summary with total/successful/failed counters.",
        ),
        exportables: z
          .array(exportableSchema)
          .describe(
            "Shared exportable manifest. These entries are independent from page-level manifests.",
          ),
      })
      .describe("Created export summary."),
    pages: z
      .array(exportPageSchema)
      .describe(
        "Strict page payload associated with this export, including per-page manifest files list.",
      ),
    guidance: runtimeGuidanceSchema
      .optional()
      .describe(
        "Mandatory runtime guidance for local artifacts created during export. Present only on successful tool results.",
      ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: create_component_export
// ============================================================

export const toolCreateComponentExport = {
  title: "PrimeUI Create Component Export",
  description: `Create a component-track export for one known PrimeUI componentId and wait for completion. This uses the component registry export API and returns a component-aware sidecar manifest plus the virtual page manifest created by Studio.

WHEN TO USE: Use this only for the component-registry import track when the user wants to export a single known componentId. This is separate from create_export, which exports selected project pages.

AFTER CALLING:
- Verify component.componentId matches the requested componentId.
- Verify component.isReadyToExport is true and page.manifest.files is present.
- Pass propsValidationId from component_props_validate when exporting a validated instance. Omit it only for the intentional default-props component export path.
- Local component sidecar manifest is saved to .primeui/temp/exports/[exportId].manifest.json.
- Use the returned export ID to call download_component_export.
- Use copy_registry_component after download_component_export for this component-track manifest. Do not use page copy tools for component-track manifests.
- If guidance is present in the result, read guidance.artifacts and follow guidance.requiredActions before continuing.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    componentId: z
      .string()
      .min(1)
      .describe("PrimeUI component registry identifier to export."),
    propsValidationId: z
      .string()
      .trim()
      .min(1)
      .optional()
      .describe(
        "Optional props validation handoff id returned by component_props_validate. Omit to export registry defaultProps.",
      ),
  },
  outputSchema: z.object({
    exportId: z
      .string()
      .describe(
        "Freshly created component export identifier. Use this as download_component_export input.id.",
      ),
    export: z
      .object({
        id: z.string().describe("Created export identifier."),
        status: exportStatusSchema,
        createdAt: z
          .string()
          .describe("Export creation timestamp in ISO-8601 UTC format."),
        expiresAt: z
          .string()
          .nullable()
          .describe(
            "Export expiration timestamp in ISO-8601 UTC format, or null when export has no explicit expiration.",
          ),
        summary: exportSummarySchema.describe(
          "Export run summary with total/successful/failed counters.",
        ),
        exportables: z
          .array(exportableSchema)
          .describe("Shared exportables included in this export payload."),
      })
      .describe("Created component export summary."),
    component: componentExportComponentSchema,
    block: copyComponentSelectedBlockSchema
      .optional()
      .describe(
        "Selected virtual component-export block metadata when the export used a propsValidationId handoff.",
      ),
    page: componentExportPageSchema.describe(
      "Virtual page manifest for this component export.",
    ),
    pages: z
      .array(componentExportPageSchema)
      .describe(
        "Page-compatible virtual page payload associated with this component export.",
      ),
    guidance: runtimeGuidanceSchema
      .optional()
      .describe(
        "Mandatory runtime guidance for local artifacts created during component export.",
      ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: download_export
// ============================================================

export const toolDownloadExport = {
  title: "PrimeUI Download Export",
  description: `Download a completed export into .primeui/temp/exports/[exportId]. Downloads and extracts the full export as a standalone Next.js project into the temp directory. This does NOT modify the user's project - files land only in .primeui/temp/.

Do NOT call this as the first step. Requires export ID from create_export. Start with get_project_info instead.

WHEN TO USE: Call this AFTER create_export has returned a completed export. Requires export ID from create_export so MCP can use the matching local sidecar manifest.

NOTE:
- Download of an export ID without local sidecar manifest will fail.
- For previous exports, create a fresh export first when possible.

AFTER DOWNLOAD:
- Use manifestPath from this response as the contract for page slug/path mapping and manifest file lists in copy operations.
- For each selected page, call copy_page with:
  - originPageSlug (required).
- For each selected block-level component instance, call copy_component with:
  - originPageSlug (required),
  - blockId (required),
  - componentId (required).
- For each selected shared exportable, call copy_exportable with:
  - exportableKey (required).
- Treat conflicts as normal and expected. If copy_page returns needs_review, stop follow-up integration and resolve manually first.
- If copy_component returns needs_review, stop and resolve the primary-file or direct-conflict issue before continuing block-level integration.
- If copy_exportable returns not_available, stop and tell the user the shared exportable must be enabled or configured in PrimeUI first.
- Do not force cleanup at flow end; keep downloaded export files available for validation and follow-up checks.
- If guidance is present in the result, read guidance.artifacts and follow guidance.requiredActions before choosing the next copy tool.

  ${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    id: z
      .string()
      .describe(
        "Export identifier to download. Prefer export.id from create_export; use list_exports only on explicit user request.",
      ),
  },
  outputSchema: z.object({
    exportId: z
      .string()
      .describe(
        "Downloaded export identifier. Should match the requested input.id for traceability.",
      ),
    projectPath: z
      .string()
      .describe(
        "Absolute local path to extracted export root in .primeui/temp/exports/[exportId]. Read files from here only.",
      ),
    manifestPath: z
      .string()
      .describe(
        "Absolute path to sidecar export manifest file (.primeui/temp/exports/[exportId].manifest.json) saved by create_export and required by copy_page and copy_component.",
      ),
    guidance: runtimeGuidanceSchema
      .optional()
      .describe(
        "Mandatory runtime guidance for the downloaded export and manifest artifacts. Present only on successful tool results.",
      ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: download_component_export
// ============================================================

export const toolDownloadComponentExport = {
  title: "PrimeUI Download Component Export",
  description: `Download a completed component-track export into .primeui/temp/exports/[exportId]. Downloads and extracts the full export archive through the existing PrimeUI export download endpoint, but requires a local component sidecar manifest created by create_component_export.

Do NOT call this as the first step. Requires export ID from create_component_export.

WHEN TO USE: Call this AFTER create_component_export has returned a completed component export. This does NOT modify the user's project - files land only in .primeui/temp/.

NOTE:
- Download of an export ID without a local component sidecar manifest will fail.
- A normal page export manifest from create_export is not accepted here.
- The next component-track step is copy_registry_component. Do not use page copy tools for component-track manifests.
- If guidance is present in the result, read guidance.artifacts and follow guidance.requiredActions before continuing.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    id: z
      .string()
      .describe(
        "Component export identifier to download. Use export.id from create_component_export.",
      ),
  },
  outputSchema: z.object({
    exportId: z
      .string()
      .describe(
        "Downloaded component export identifier. Should match the requested input.id.",
      ),
    projectPath: z
      .string()
      .describe(
        "Absolute local path to extracted export root in .primeui/temp/exports/[exportId]. Read files from here only.",
      ),
    manifestPath: z
      .string()
      .describe(
        "Absolute path to component sidecar export manifest saved by create_component_export.",
      ),
    guidance: runtimeGuidanceSchema
      .optional()
      .describe(
        "Mandatory runtime guidance for the downloaded component export and manifest artifacts.",
      ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: copy_page
// ============================================================

export const toolCopyPage = {
  title: "PrimeUI Copy Page",
  description: `Safely copy one page from downloaded export into the user's local project. Works only with local files in .primeui/temp/exports and does NOT call PrimeUI API.

WHEN TO USE:
- Call this AFTER download_export.
- Call once per selected page.

BEHAVIOR:
- Reads export sidecar manifest to resolve originPageSlug -> pagePath/componentsPath.
- Copies and validates files strictly from pages[].manifest.files for the selected page.
- Never overwrites existing conflicting files.
- Writes full conflict diffs to a local report file in .primeui/temp/exports/[exportId].copy-report-[copyId].json.
- Reports:
  a) copied new files (relative target paths),
  b) identical existing files (relative target paths),
  c) conflicting files as lightweight metadata (relative target paths + binary flag, no inline diff),
  d) missing dependencies added to package.json,
  e) dependency version conflicts (report-only, no auto upgrade).
- Adds only missing dependencies to package.json (dependencies/devDependencies/peerDependencies).
- Never updates existing dependency versions and never runs install commands.
- If status is needs_review, page import is NOT complete and manual review is required before continuing. This can be caused by file conflicts or dependency version conflicts.
- If guidance is present in the result, read guidance.artifacts and follow guidance.requiredActions before continuing integration.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    originPageSlug: z
      .string()
      .describe(
        "Source page slug from PrimeUI project pages list, for example '/', '/pricing', '/docs'.",
      ),
  },
  outputSchema: z.object({
    status: z
      .enum(["completed", "needs_review"])
      .describe(
        "Decision state for next step. needs_review means manual review is required before follow-up integration because of file conflicts or dependency version conflicts.",
      ),
    message: z
      .string()
      .describe(
        "Decision-oriented summary that includes resolved reportPath and manual follow-up instructions when needed.",
      ),
    reportPath: z
      .string()
      .describe(
        "Absolute path to saved local report with full conflict diffs and details.",
      ),
    exportId: z
      .string()
      .describe("Resolved local export identifier used for copy operation."),
    exportPath: z
      .string()
      .describe("Absolute path to local extracted export project root."),
    originPageSlug: z
      .string()
      .describe("Normalized source page slug requested for copy."),
    sourcePagePath: z
      .string()
      .describe("Source page path relative to export root."),
    sourceComponentsPath: z
      .string()
      .describe("Source components directory relative to export root."),
    newFiles: z
      .array(z.string())
      .describe(
        "Relative target paths for new files copied into user project.",
      ),
    identicalFiles: z
      .array(z.string())
      .describe("Relative target paths for existing byte-identical files."),
    conflictFiles: z
      .array(conflictFileSchema)
      .describe(
        "Files that already exist and differ from export version. Never overwritten.",
      ),
    addedDependencies: z
      .array(dependencyToAddSchema)
      .describe(
        "Dependencies that were missing and have been added to user's package.json.",
      ),
    dependenciesVersionConflicts: z
      .array(dependencyVersionConflictSchema)
      .describe(
        "Dependencies with version mismatch between export and user project. Report only.",
      ),
    summary: z.object({
      totalCandidateFiles: z
        .number()
        .describe(
          "Total number of candidate files considered for page copy from pages[].manifest.files.",
        ),
      copiedFiles: z.number().describe("Number of files copied as new."),
      identicalFiles: z
        .number()
        .describe("Number of files detected as identical."),
      conflictFiles: z
        .number()
        .describe("Number of conflicting files not copied."),
      addedDependencies: z
        .number()
        .describe("Count of dependencies added to package.json."),
      dependenciesVersionConflicts: z
        .number()
        .describe("Count of dependency version mismatches reported."),
    }),
    guidance: runtimeGuidanceSchema
      .optional()
      .describe(
        "Mandatory runtime guidance for export artifacts and next steps after this page copy result. Present only on successful tool results.",
      ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
  },
};

// ============================================================
// Tool: copy_component
// ============================================================

export const toolCopyComponent = {
  title: "PrimeUI Copy Component",
  description: `Safely copy one block-level component from downloaded export into the user's local project. Works only with local files in .primeui/temp/exports and uses inspect_page semantics for block selection.

WHEN TO USE:
- Call this AFTER download_export.
- Call this when the user wants one selected block instance, not a full page transfer.
- Pass both blockId and componentId so the tool can bind exact props for the selected instance while still resolving the canonical component file identity.

BEHAVIOR:
- Reads export sidecar manifest to resolve originPageSlug -> pagePath/componentsPath.
- Matches the selected block from inspect_page-compatible page details using blockId + componentId.
- Computes selectedBlock.occurrenceIndex and selectedBlock.contentKey for duplicate component types on the same page.
- Reuses the most recent inspect_page snapshot for selected block metadata when available instead of re-fetching live page details at copy time.
- Resolves the primary component file from downloaded export using:
  - [componentsPath]/[componentId].tsx
  - [componentsPath]/[componentId]/index.tsx
- Copies only the reliably resolved primary component file.
- Scans the selected component tree for external package imports, adds missing dependencies to package.json, and reports version conflicts.
- Never overwrites an existing conflicting primary component file.
- Writes full direct and potential conflict details to a local report file in .primeui/temp/exports/[exportId].copy-component-report-[copyId].json.
- Reports:
  a) selectedBlock with exact props for the chosen instance,
  b) primaryComponentFile when resolved,
  c) copied new or identical primary files,
  d) conflictFiles only for files actually attempted during copy,
  e) addedDependencies / dependenciesVersionConflicts for dependency follow-up,
  f) manualInsertionTargets for likely integration points,
  g) potentialDependencyFiles / potentialConflictFiles as prioritized hints for manual review.
- If status is needs_review, stop and resolve the reported primary-file, missing-manifest-file, or dependency-version issue before treating the component transfer as complete.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    originPageSlug: z
      .string()
      .describe(
        "Source page slug from PrimeUI project pages list, for example '/', '/pricing', '/docs'.",
      ),
    blockId: z
      .string()
      .describe(
        "Selected block instance identifier from inspect_page output. Required when the same componentId appears multiple times on one page.",
      ),
    componentId: z
      .string()
      .describe(
        "Selected component identity from inspect_page output (block.key). This defines the canonical component file identity.",
      ),
  },
  outputSchema: z.object({
    status: z
      .enum(["completed", "needs_review"])
      .describe(
        "Decision state for next step. needs_review means the primary component file could not be resolved safely or directly copied without conflict.",
      ),
    message: z
      .string()
      .describe(
        "Decision-oriented summary with reportPath and the next manual actions for completing component transfer.",
      ),
    reportPath: z
      .string()
      .describe(
        "Absolute path to saved local report with direct conflicts, potential conflicts, and selected block metadata.",
      ),
    exportId: z
      .string()
      .describe("Resolved local export identifier used for component copy."),
    exportPath: z
      .string()
      .describe("Absolute path to local extracted export project root."),
    manifestPath: z
      .string()
      .describe(
        "Absolute path to the local sidecar export manifest used during component copy.",
      ),
    originPageSlug: z
      .string()
      .describe("Normalized source page slug requested for copy."),
    blockId: z
      .string()
      .describe("Normalized selected block instance identifier."),
    componentId: z
      .string()
      .describe("Normalized component file identity requested for copy."),
    selectedBlock: copyComponentSelectedBlockSchema.describe(
      "Resolved selected block instance with exact props and duplicate-instance metadata.",
    ),
    primaryComponentFile: z
      .string()
      .nullable()
      .describe(
        "Relative target path for the canonical component file when resolved, otherwise null.",
      ),
    manualInsertionTargets: z
      .array(z.string())
      .describe(
        "Relative target paths that usually need manual wiring for this component instance, typically the page file.",
      ),
    newFiles: z
      .array(z.string())
      .describe(
        "Relative target paths for new files copied into user project. For copy_component this usually contains only the primary component file.",
      ),
    identicalFiles: z
      .array(z.string())
      .describe(
        "Relative target paths for existing byte-identical copied files.",
      ),
    conflictFiles: z
      .array(conflictFileSchema)
      .describe(
        "Files that were actually attempted during copy and already exist with different content. Never overwritten.",
      ),
    addedDependencies: z
      .array(dependencyToAddSchema)
      .describe(
        "Dependencies that were missing and have been added to user's package.json.",
      ),
    dependenciesVersionConflicts: z
      .array(dependencyVersionConflictSchema)
      .describe(
        "Dependencies with version mismatch between export and user project. Report only.",
      ),
    potentialDependencyFiles: z
      .array(z.string())
      .describe(
        "Prioritized hint files related to the selected page manifest. Review them first if the copied component is incomplete.",
      ),
    potentialConflictFiles: z
      .array(z.string())
      .describe(
        "Potential dependency files that already differ locally. These are hints only and are not treated as direct copy conflicts.",
      ),
    guidance: copyComponentGuidanceSchema.describe(
      "Structured follow-up guidance for completing block-level transfer with the selected props and related files.",
    ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
  },
};

// ============================================================
// Tool: copy_registry_component
// ============================================================

export const toolCopyRegistryComponent = {
  title: "PrimeUI Copy Registry Component",
  description: `Safely copy one registry component from a downloaded component export into the user's local project. Works only with component-track files in .primeui/temp/exports and does NOT call PrimeUI API.

WHEN TO USE:
- Call this AFTER create_component_export and download_component_export.
- Use this for component-registry exports, not page exports.
- Pass componentId from the create_component_export result so the local component sidecar manifest can be validated before copying.

BEHAVIOR:
- Reads the component sidecar manifest saved by create_component_export.
- Validates that manifest.component.componentId matches input.componentId.
- Copies only the selected registry component import graph, narrowly referenced public assets, and safe support files from the component sidecar manifest/export.
- Remaps component-local files into src/components/shared/** and returns componentImportPath for manual insertion.
- Skips virtual route files that include __primeui-component-export, because those are reference/insertion hints and must not be installed as normal app routes.
- Returns selectedBlock and insertion artifacts derived from the downloaded virtual component export. Treat insertion.propsCode and insertion.jsx as the materialized instance source of truth.
- Skips app/search/layout/header/footer/env/project-shell files instead of copying whole exportable buckets.
- Never copies package.json directly. package.json is used only to add missing dependency entries and report dependency version conflicts.
- Keeps safe support files on their natural target paths, such as src/components/ui/**, src/lib/**, src/types/**, and src/contexts/**.
- Never overwrites existing conflicting files.
- Writes full conflict details to .primeui/temp/exports/[exportId].copy-registry-component-report-[copyId].json.
- Reports copied files, identical files, conflicts, skipped files, dependency additions, dependency version conflicts, missing manifest files, and manual insertion guidance.
- If status is needs_review, resolve conflicts, missing manifest files, or dependency version conflicts before treating transfer as complete.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    componentId: z
      .string()
      .describe(
        "PrimeUI component registry identifier from create_component_export output. Must match the local component sidecar manifest.",
      ),
  },
  outputSchema: z.object({
    status: z
      .enum(["completed", "needs_review"])
      .describe(
        "Decision state for next step. needs_review means conflicts, dependency version conflicts, missing manifest files, or no copyable files require manual review.",
      ),
    message: z
      .string()
      .describe(
        "Decision-oriented summary with reportPath and manual insertion guidance.",
      ),
    reportPath: z
      .string()
      .describe(
        "Absolute path to saved local report with conflicts, skipped files, and component manifest metadata.",
      ),
    exportId: z
      .string()
      .describe("Resolved local component export identifier used for copy."),
    exportPath: z
      .string()
      .describe("Absolute path to local extracted component export root."),
    manifestPath: z
      .string()
      .describe(
        "Absolute path to the local component sidecar manifest used during copy.",
      ),
    component: componentExportComponentSchema.describe(
      "Component metadata from the validated component sidecar manifest.",
    ),
    componentImportPath: z
      .string()
      .describe(
        "Canonical import path for the copied registry component entrypoint, for example @/components/shared/hero--pricing.",
      ),
    selectedBlock: copyComponentSelectedBlockSchema.describe(
      "Selected virtual component-export block metadata. First-instance contentKey semantics match page export.",
    ),
    insertion: registryComponentInsertionSchema.describe(
      "Materialized insertion artifact for adding this component instance to a real local page.",
    ),
    manifestFiles: z
      .array(z.string())
      .describe(
        "Normalized component sidecar manifest files considered by the copy service.",
      ),
    copyableFiles: z
      .array(z.string())
      .describe(
        "Relative target paths considered for copying after component remapping and safety filtering.",
      ),
    newFiles: z
      .array(z.string())
      .describe("Relative target paths for new files copied."),
    identicalFiles: z
      .array(z.string())
      .describe("Relative target paths for existing byte-identical files."),
    conflictFiles: z
      .array(conflictFileSchema)
      .describe(
        "Files that already exist and differ from export version. Never overwritten.",
      ),
    skippedFiles: z
      .array(skippedCopyFileSchema)
      .describe(
        "Manifest files intentionally not copied, such as package.json or virtual component-export routes.",
      ),
    addedDependencies: z
      .array(dependencyToAddSchema)
      .describe(
        "Dependencies that were missing and have been added to user's package.json.",
      ),
    dependenciesVersionConflicts: z
      .array(dependencyVersionConflictSchema)
      .describe(
        "Dependencies with version mismatch between export and user project. Report only.",
      ),
    missingManifestFiles: z
      .array(z.string())
      .describe(
        "Manifest file paths that were expected but missing from the downloaded export.",
      ),
    summary: z.object({
      totalManifestFiles: z.number(),
      totalCopyableFiles: z.number(),
      copiedFiles: z.number(),
      identicalFiles: z.number(),
      conflictFiles: z.number(),
      skippedFiles: z.number(),
      addedDependencies: z.number(),
      dependenciesVersionConflicts: z.number(),
      missingManifestFiles: z.number(),
    }),
    guidance: runtimeGuidanceSchema.describe(
      "Structured follow-up guidance. It always instructs manual insertion into a real target page and warns that the virtual route was skipped.",
    ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
  },
};

// ============================================================
// Tool: copy_exportable
// ============================================================

export const toolCopyExportable = {
  title: "PrimeUI Copy Exportable",
  description: `Safely copy one shared exportable from downloaded export into the user's local project. Works only with local files in .primeui/temp/exports and does NOT call PrimeUI API.

WHEN TO USE:
- Call this AFTER download_export.
- Call once per selected shared exportable.

BEHAVIOR:
- Reads export sidecar manifest to resolve exportableKey -> export.exportables[] entry.
- Copies and validates files strictly from export.exportables[].manifest.files for the selected shared exportable.
- Never copies package.json automatically. package.json is treated as dependency guidance only.
- Returns status not_available when the shared exportable is disabled, not configured, or currently has no copyable export files.
- Never overwrites existing conflicting files.
- Writes full conflict diffs to a local report file in .primeui/temp/exports/[exportId].copy-exportable-report-[copyId].json.
- Reports:
  a) copied new files (relative target paths),
  b) identical existing files (relative target paths),
  c) conflicting files as lightweight metadata (relative target paths + binary flag, no inline diff),
  d) skipped files such as package.json,
  e) missedDependencies that must be handled explicitly,
  f) dependency version conflicts (report-only, no auto upgrade).
- missedDependencies are a substantial and inseparable part of the shared exportable functionality. They are never added or installed automatically.
- If status is needs_review, shared exportable transfer is NOT complete and manual review is required before continuing.

${WORKFLOW_SUMMARY}`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    exportableKey: z
      .string()
      .describe(
        "Shared exportable key from PrimeUI export payload, for example 'logo', 'theme', 'cookieBanner', 'announcementBanner', or 'imageAssets'.",
      ),
  },
  outputSchema: z.object({
    status: z
      .enum(["completed", "needs_review", "not_available"])
      .describe(
        "Decision state for next step. not_available means the shared exportable must be enabled or configured in PrimeUI first. needs_review means file conflicts or dependency version conflicts require manual resolution.",
      ),
    message: z
      .string()
      .describe(
        "Decision-oriented summary with resolved reportPath and mandatory follow-up guidance for availability, conflicts, or dependency handling.",
      ),
    reportPath: z
      .string()
      .nullable()
      .describe(
        "Absolute path to saved local report with full conflict diffs and details, or null when the exportable is not available for copy.",
      ),
    exportId: z
      .string()
      .describe("Resolved local export identifier used for copy operation."),
    exportPath: z
      .string()
      .describe("Absolute path to local extracted export project root."),
    exportableKey: z
      .string()
      .describe("Normalized shared exportable key requested for copy."),
    isReadyToExport: z
      .boolean()
      .describe(
        "Readiness flag from export manifest for the selected shared exportable.",
      ),
    manifestSuccess: z
      .boolean()
      .describe(
        "Success flag from export manifest for the selected shared exportable.",
      ),
    manifestMessage: z
      .string()
      .describe(
        "Manifest message from export payload for the selected shared exportable.",
      ),
    manifestFiles: z
      .array(z.string())
      .describe(
        "Authoritative list of manifest files for this shared exportable before any copy-time filtering.",
      ),
    newFiles: z
      .array(z.string())
      .describe(
        "Relative target paths for new files copied into user project.",
      ),
    identicalFiles: z
      .array(z.string())
      .describe("Relative target paths for existing byte-identical files."),
    conflictFiles: z
      .array(conflictFileSchema)
      .describe(
        "Files that already exist and differ from export version. Never overwritten.",
      ),
    skippedFiles: z
      .array(skippedCopyFileSchema)
      .describe(
        "Manifest files intentionally not copied, for example package.json which is handled via dependency guidance.",
      ),
    missedDependencies: z
      .array(dependencyToAddSchema)
      .describe(
        "Required packages missing in user's package.json. These are never added or installed automatically.",
      ),
    dependenciesVersionConflicts: z
      .array(dependencyVersionConflictSchema)
      .describe(
        "Dependencies with version mismatch between export and user project. Report only.",
      ),
    summary: z.object({
      totalManifestFiles: z
        .number()
        .describe(
          "Total number of manifest files listed for this shared exportable before copy-time filtering.",
        ),
      totalCandidateFiles: z
        .number()
        .describe(
          "Total number of candidate files considered for direct copy after filtering skipped files such as package.json.",
        ),
      copiedFiles: z.number().describe("Number of files copied as new."),
      identicalFiles: z
        .number()
        .describe("Number of files detected as identical."),
      conflictFiles: z
        .number()
        .describe("Number of conflicting files not copied."),
      skippedFiles: z
        .number()
        .describe("Number of manifest files intentionally skipped."),
      missedDependencies: z
        .number()
        .describe("Count of missing dependency packages reported."),
      dependenciesVersionConflicts: z
        .number()
        .describe("Count of dependency version mismatches reported."),
    }),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: false,
  },
};

// ============================================================
// Tool: clear_temp
// ============================================================

export const toolClearTemp = {
  title: "PrimeUI Clear Temp",
  description: `Delete all files in .primeui/temp/ and recreate empty temp structure.

Call this at the START of a new import flow, before get_project_info/create_export, to reset stale temp state from previous runs.

WHEN TO USE: Call once at the beginning of each new import flow. Do not call this between page imports in the same flow.

Safe to call multiple times. Only affects .primeui/temp/ directory - never touches the user's project files.

${WORKFLOW_SUMMARY}`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: z.object({
    success: z
      .boolean()
      .describe(
        "True when temp cleanup completed and baseline temp structure was recreated.",
      ),
  }),
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
};

const projectDescriptionOutputSchema = z.object({
  projectDescription: z
    .string()
    .describe(
      "Current shared project description string. Empty string means the description is not set.",
    ),
});

const projectApiWireframeStatusSchema = z
  .enum(["pending", "generating", "ready", "error"])
  .describe(
    "Shared page readiness status from the external API. ready means export-selected content exists; generating means background AI work is still running.",
  );

const projectApiVariantSummarySchema = z.object({
  id: z
    .string()
    .describe("Stable variant identifier within the scoped PrimeUI project."),
  name: z.string().describe("Human-readable variant name."),
  isActive: z
    .boolean()
    .describe(
      "True when this variant is currently selected as the page active variant for export.",
    ),
  isGenerating: z
    .boolean()
    .describe(
      "True when background AI generation is still running for this variant.",
    ),
});

const pageGenerationTypeSchema = z
  .enum([
    "landing",
    "pricing",
    "blogIndex",
    "blogPost",
    "contact-us",
    "docs",
    "legal",
  ])
  .describe(
    "Supported PrimeUI page type. Must match the external API page type enum.",
  );

const componentCandidateOperationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("append"),
  }),
  z.object({
    type: z.literal("insert"),
    index: z
      .number()
      .int()
      .describe("Zero-based insertion index in the ordered blocks list."),
  }),
  z.object({
    type: z.literal("replace"),
    index: z
      .number()
      .int()
      .describe("Zero-based index of the block being replaced."),
  }),
]);

const componentCandidatesGetInputSchema = z.object({
  projectRoot: projectRootInputSchema,
  pageSlug: z
    .string()
    .refine((value) => value.trim().length > 0, {
      message: "pageSlug must be a non-empty string",
    })
    .describe(
      "Stable PrimeUI page slug used for candidate scoring and automatic diagnostics, for example '/pricing'.",
    ),
  pageType: pageGenerationTypeSchema,
  blocks: z
    .array(
      z.object({
        componentId: z
          .string()
          .describe("PrimeUI component id already present in page order."),
      }),
    )
    .describe(
      "Current ordered page composition before applying the requested operation.",
    ),
  operation: componentCandidateOperationSchema
    .optional()
    .default({ type: "append" })
    .describe(
      "Candidate placement operation. Omit for the normal append flow.",
    ),
  count: z
    .number()
    .int()
    .positive()
    .optional()
    .describe("Optional maximum candidate count. Studio defaults to 8."),
  constraints: z
    .object({
      spreadDegree: z
        .enum(["any-group", "same-group", "same-family"])
        .optional()
        .describe("Optional UX scoring spread override."),
      allowedGroups: z
        .array(z.string())
        .optional()
        .describe("Optional allow-list of candidate groups."),
      excludedGroups: z
        .array(z.string())
        .optional()
        .describe("Optional deny-list of candidate groups."),
      excludeComponentIds: z
        .array(z.string())
        .optional()
        .describe("Optional component ids to remove from the candidate pool."),
    })
    .optional()
    .describe("Optional advanced candidate pool and spread constraints."),
});

export const componentCandidatesGetValidationSchema =
  componentCandidatesGetInputSchema;

const componentPropsValidateInputSchema = z.object({
  projectRoot: projectRootInputSchema,
  pageSlug: z
    .string()
    .refine((value) => value.trim().length > 0, {
      message: "pageSlug must be a non-empty string",
    })
    .describe(
      "Stable PrimeUI page slug used for validation diagnostics, for example '/pricing'. Use the same pageSlug as candidate retrieval.",
    ),
  componentId: z
    .string()
    .describe("PrimeUI component registry identifier selected by the agent."),
  blockId: z
    .string()
    .trim()
    .min(1)
    .describe(
      "Stable local block identifier for this planned component instance. Required for propsValidationId handoff records.",
    ),
  props: z
    .record(z.unknown())
    .describe(
      "Agent-authored props payload to validate against PrimeUI's registry schema.",
    ),
});

export const componentPropsValidateValidationSchema =
  componentPropsValidateInputSchema;

const componentCandidateLayoutSchema = z.object({
  container: z.unknown().describe("Layout container metadata."),
  textAlignment: z.unknown().describe("Text alignment metadata."),
  bottomWeight: z.unknown().describe("Bottom visual weight metadata."),
  entryWidth: z.unknown().describe("Incoming width rhythm metadata."),
  exitWidth: z.unknown().describe("Outgoing width rhythm metadata."),
  orientation: z.unknown().describe("Optional orientation metadata."),
  structure: z.unknown().describe("Optional structure metadata."),
});

const componentCandidateSchema = z.object({
  componentId: z
    .string()
    .describe("PrimeUI component registry identifier to choose or export."),
  name: z.string().describe("Human-readable component name."),
  group: z.string().describe("Component group used by candidate scoring."),
  familyId: z.string().describe("Component family identifier from catalog."),
  uxScore: z.number().describe("Total UX score used for ranking."),
  insertionScore: z
    .number()
    .describe("Score for inserting this candidate at the requested position."),
  followingScore: z
    .number()
    .describe("Neighbor compatibility score with the following block."),
  layout: componentCandidateLayoutSchema.describe(
    "Layout metadata useful for choosing visual rhythm.",
  ),
  defaultProps: z
    .unknown()
    .describe("Registry default props, when available from PrimeUI."),
  exportReady: z
    .boolean()
    .describe("True when the component is expected to be exportable."),
  copyHints: z
    .object({
      deliveryToolchain: z
        .array(z.string())
        .describe(
          "Recommended tool sequence for delivering this registry component locally.",
        ),
    })
    .describe("Machine-readable delivery hints for the selected candidate."),
  description: z.string().nullable().optional(),
  functionality: z.string().nullable().optional(),
  impression: z.string().nullable().optional(),
  visualStyle: z.unknown().optional(),
  compactSchema: z.string().nullable().optional(),
  jsonSchema: z.record(z.unknown()).nullable().optional(),
});

const componentCandidatesGetOutputSchema = z.object({
  request: z
    .object({
      pageSlug: z.string(),
      pageType: z.string(),
      operation: componentCandidateOperationSchema,
      count: z.number(),
    })
    .describe("Normalized request echo returned by PrimeUI."),
  candidates: z
    .array(componentCandidateSchema)
    .describe("Ranked rich candidate shortlist returned by PrimeUI."),
});

const componentPropsValidationErrorSchema = z.object({
  path: z
    .string()
    .describe("JSON path or root path for the invalid props location."),
  message: z
    .string()
    .describe("Actionable validation message returned by PrimeUI."),
});

const componentPropsRenderCheckSchema = z.object({
  attempted: z
    .boolean()
    .describe("True when PrimeUI attempted a render smoke check."),
  passed: z.boolean().describe("True when the render smoke check passed."),
  message: z
    .string()
    .describe("Human-readable render check status or skip reason."),
});

const componentPropsValidateOutputSchema = z.object({
  valid: z.boolean().describe("True when props passed schema validation."),
  normalizedProps: z
    .record(z.unknown())
    .nullable()
    .describe(
      "Props normalized by PrimeUI when valid; null when validation failed.",
    ),
  propsValidationId: z
    .string()
    .nullable()
    .describe(
      "Durable validation handoff id for create_component_export; null when props are invalid.",
    ),
  block: z
    .object({
      blockId: z.string(),
      componentId: z.string(),
      contentKey: z.string(),
      props: z.record(z.unknown()),
    })
    .nullable()
    .describe(
      "Selected validated block identity and normalized props; null when props are invalid.",
    ),
  errors: z
    .array(componentPropsValidationErrorSchema)
    .describe("Structured validation errors for invalid props."),
  hints: z
    .array(z.string())
    .describe("Agent-facing guidance for correcting invalid props."),
  renderCheck: componentPropsRenderCheckSchema.describe(
    "Optional render-smoke status returned by PrimeUI.",
  ),
});

const projectApiPageResourceSchema = z.object({
  id: z.string().describe("Stable PrimeUI page identifier."),
  title: z.string().describe("Human-readable page title."),
  slug: z
    .string()
    .describe("PrimeUI route slug for the page, for example '/', '/pricing'."),
  pageType: pageGenerationTypeSchema,
  isReadyToExport: z
    .boolean()
    .describe(
      "True when the page currently has an export-selected active variant.",
    ),
  wireframeStatus: projectApiWireframeStatusSchema,
  variants: z
    .array(projectApiVariantSummarySchema)
    .describe("Lightweight variant summaries attached to the page resource."),
});

const projectApiPagesOutputSchema = z.object({
  pages: z
    .array(projectApiPageResourceSchema)
    .describe(
      "Project pages payload returned by the external API. GET list may include many entries; create/get/set-active responses usually return one entry.",
    ),
});

const projectApiVariantsOutputSchema = z.object({
  variants: z
    .array(projectApiVariantSummarySchema)
    .describe("Variant summaries for one selected page."),
});

const projectApiVariantSummaryResultSchema = z.object({
  variant: projectApiVariantSummarySchema.describe(
    "Variant summary returned immediately after creation.",
  ),
});

const projectApiVariantOutputSchema = z.object({
  variant: z.object({
    id: z
      .string()
      .describe("Stable variant identifier within the selected page."),
    name: z.string().describe("Human-readable variant name."),
    isActive: z
      .boolean()
      .describe("True when this variant is currently active for export."),
    isGenerating: z
      .boolean()
      .describe(
        "True while background AI generation for this variant is still running.",
      ),
    components: z
      .array(pageComponentSchema)
      .describe("Resolved component payload for the selected variant."),
  }),
});

const issueReportSubmitOutputSchema = z.object({
  accepted: z
    .literal(true)
    .describe("Always true when the issue report was accepted by the API."),
  ticketId: z
    .string()
    .describe("Generated issue report identifier returned by the API."),
});

const createContentModeSchema = z
  .enum(["ai", "empty", "components"])
  .describe(
    "Content creation mode. empty creates an empty resource, components uses ordered componentIds, ai queues generation from prompt.",
  );

const createPageOrVariantSchema = <T extends z.ZodRawShape>(shape: T) =>
  z.object(shape).superRefine((value, ctx) => {
    const contentMode = value.contentMode;
    const componentIds = value.componentIds;
    const prompt = value.prompt;

    if (contentMode === "ai") {
      if (Array.isArray(componentIds) && componentIds.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "componentIds are not allowed in ai mode.",
          path: ["componentIds"],
        });
      }
      return;
    }

    if (contentMode === "empty") {
      if (Array.isArray(componentIds) && componentIds.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "componentIds are not allowed in empty mode.",
          path: ["componentIds"],
        });
      }
      if (typeof prompt === "string" && prompt.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "prompt is not allowed in empty mode.",
          path: ["prompt"],
        });
      }
      return;
    }

    if (!Array.isArray(componentIds) || componentIds.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "componentIds are required in components mode.",
        path: ["componentIds"],
      });
    }
    if (typeof prompt === "string" && prompt.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "prompt is not allowed in components mode.",
        path: ["prompt"],
      });
    }
  });

const projectDescriptionUpsertInputSchema = z.object({
  projectRoot: projectRootInputSchema,
  projectDescription: z
    .string()
    .describe(
      "Project description to store. Empty or whitespace-only input clears the saved description.",
    ),
});

const projectPageCreateBaseInputSchema = z.object({
  projectRoot: projectRootInputSchema,
  slug: z
    .string()
    .describe("Route slug for the new page, for example '/pricing'."),
  title: z.string().describe("Human-readable page title."),
  pageType: pageGenerationTypeSchema,
  contentMode: createContentModeSchema,
  componentIds: z
    .array(z.string())
    .optional()
    .describe(
      "Ordered component IDs for components mode. Omit for empty or ai mode.",
    ),
  prompt: z
    .string()
    .optional()
    .describe("Optional AI prompt used only in ai mode."),
});

export const projectPageCreateValidationSchema = createPageOrVariantSchema(
  projectPageCreateBaseInputSchema.shape,
);

const projectPageVariantCreateBaseInputSchema = z.object({
  projectRoot: projectRootInputSchema,
  pageId: z.string().describe("Target page identifier for the new variant."),
  name: z
    .string()
    .optional()
    .describe(
      "Optional explicit variant name. When omitted, Studio assigns one.",
    ),
  contentMode: createContentModeSchema,
  componentIds: z
    .array(z.string())
    .optional()
    .describe(
      "Ordered component IDs for components mode. Omit for empty or ai mode.",
    ),
  prompt: z
    .string()
    .optional()
    .describe("Optional AI prompt used only in ai mode."),
});

export const projectPageVariantCreateValidationSchema =
  createPageOrVariantSchema(projectPageVariantCreateBaseInputSchema.shape);

const projectIssueReportSubmitInputSchema = z.object({
  projectRoot: projectRootInputSchema,
  reportText: z
    .string()
    .describe("Issue report text to submit for the scoped project."),
  tags: z
    .array(z.string())
    .optional()
    .describe(
      "Optional issue report tags. Empty values are trimmed and dropped by the API.",
    ),
});

const searchEnvironmentSlugSchema = z
  .string()
  .describe(
    "Search environment slug, for example 'prod', 'staging', or a custom environment slug.",
  );

const searchRuntimeStatusSchema = z.enum([
  "bootstrapping",
  "active",
  "indexing",
  "failed",
  "inactive",
]);

const searchRuntimeSchema = z.object({
  createdAt: z
    .string()
    .describe("ISO timestamp when the environment was created."),
  currentMeilisearchIndexUid: z
    .string()
    .nullable()
    .describe(
      "Current Meilisearch index UID bound to this environment, when available.",
    ),
  currentQueryId: z
    .string()
    .nullable()
    .describe(
      "Current query credential or query binding identifier, when available.",
    ),
  id: z.string().describe("Stable internal environment record identifier."),
  lastErrorSummary: z
    .string()
    .nullable()
    .describe("Most recent error summary for this environment, if any."),
  lastFailedSyncAt: z
    .string()
    .nullable()
    .describe("ISO timestamp of the last failed sync for this environment."),
  lastSuccessfulSyncAt: z
    .string()
    .nullable()
    .describe(
      "ISO timestamp of the last successful sync for this environment.",
    ),
  name: z.string().describe("Human-readable environment name."),
  slug: z.string().describe("Canonical search environment slug."),
  status: searchRuntimeStatusSchema.describe(
    "Current environment lifecycle status.",
  ),
  updatedAt: z
    .string()
    .describe("ISO timestamp of the latest environment update."),
});

const searchOverviewOutputSchema = z.object({
  success: z.literal(true),
  projectId: z
    .string()
    .describe("PrimeUI project identifier for this overview."),
  runtimeModel: z
    .object({
      additionalRuntimesSupported: z.literal(true),
      defaultRuntimeAutoWired: z.literal(true),
      defaultRuntimeSlug: z.literal("prod"),
      mode: z.literal("runtime-first"),
      panelCompatibility: z.literal("disabled"),
      stagingRuntimeAutoWired: z.literal(false),
      stagingRuntimeSlug: z.literal("staging"),
    })
    .describe("Managed-search runtime model summary from the PrimeUI API."),
  search: z
    .object({
      bootstrapCompletedAt: z.string().nullable(),
      createdAt: z.string().nullable(),
      id: z.string().nullable(),
      enabled: z.boolean(),
      status: z.enum(["inactive", "active", "degraded"]),
      defaultIndexId: z.string().nullable(),
      defaultSearchId: z.string().nullable(),
      lastErrorSummary: z.string().nullable(),
      lastFailedSyncAt: z.string().nullable(),
      lastSuccessfulSyncAt: z.string().nullable(),
      updatedAt: z.string().nullable(),
    })
    .describe("Top-level managed-search root status."),
  runtimes: z
    .array(searchRuntimeSchema)
    .describe("All configured search environments for this project."),
  defaultIndex: z
    .record(z.unknown())
    .nullable()
    .describe(
      "Default index payload from the API overview, or null when absent.",
    ),
  customIndexes: z
    .array(z.record(z.unknown()))
    .describe("Additional search index payloads from the API overview."),
  recentJobs: z
    .array(z.record(z.unknown()))
    .describe("Recent search jobs from the overview API."),
  recentSyncRuns: z
    .array(z.record(z.unknown()))
    .describe("Recent sync-run payloads from the overview API."),
  sources: z
    .array(z.record(z.unknown()))
    .describe("Configured managed-search sources from the overview API."),
  lastError: z
    .record(z.unknown())
    .nullable()
    .describe("Latest overview-level managed-search error payload, if any."),
  queryProtection: z
    .record(z.unknown())
    .describe("Query protection summary from the overview API."),
  relevanceSettings: z
    .record(z.unknown())
    .describe("Relevance settings summary from the overview API."),
});

const searchEnvironmentOutputSchema = z.object({
  success: z.literal(true),
  environmentSlug: z
    .string()
    .describe("Resolved search environment slug for this focused MCP view."),
  environment: searchRuntimeSchema.describe("Selected environment record."),
  primaryIndex: z
    .record(z.unknown())
    .nullable()
    .describe(
      "Primary related index for this environment when one can be identified.",
    ),
  relatedIndexes: z
    .array(z.record(z.unknown()))
    .describe("Related search indexes inferred for this environment."),
  recentJobs: z
    .array(z.record(z.unknown()))
    .describe(
      "Recent jobs filtered to the related indexes for this environment.",
    ),
  recentSyncRuns: z
    .array(z.record(z.unknown()))
    .describe(
      "Recent sync runs filtered to the related indexes for this environment.",
    ),
  search: z
    .record(z.unknown())
    .describe(
      "Top-level managed-search root status carried through from the overview API.",
    ),
  runtimeModel: z
    .record(z.unknown())
    .describe(
      "Managed-search runtime model carried through from the overview API.",
    ),
  sources: z
    .array(z.record(z.unknown()))
    .describe("Managed-search sources carried through from the overview API."),
  lastError: z
    .record(z.unknown())
    .nullable()
    .describe("Latest overview-level managed-search error payload, if any."),
});

const searchEnvironmentMutationOutputSchema = z.object({
  success: z.literal(true),
  environment: searchRuntimeSchema.describe(
    "Environment payload returned by the PrimeUI API.",
  ),
});

const searchEnvironmentDeleteOutputSchema = z.object({
  success: z.literal(true),
});

const searchEnvironmentSyncOutputSchema = z.object({
  success: z.literal(true),
  mode: z
    .enum(["executed", "guidance"])
    .describe(
      "Whether MCP executed the real sync command or returned next-step guidance.",
    ),
  environmentSlug: z.string().describe("Resolved search environment slug."),
  command: z
    .string()
    .describe(
      "Exact command the agent should run or that MCP already executed.",
    ),
  workingDirectory: z
    .string()
    .describe("Absolute project directory where the command applies."),
  jobId: z
    .string()
    .nullable()
    .describe(
      "Search job identifier when the command was actually executed and returned one.",
    ),
  status: z
    .string()
    .nullable()
    .describe("Execution status when available; null for guidance-only mode."),
  notes: z
    .array(z.string())
    .describe(
      "Actionable notes that explain why guidance or execution was chosen.",
    ),
});

// ============================================================
// Tool: project_description_get
// ============================================================

export const toolProjectDescriptionGet = {
  title: "PrimeUI Project Description Get",
  description: `Atomic external API helper. Returns the current shared project description for the scoped PrimeUI project.

WHEN TO USE:
- Use this when the caller needs only the project description text.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- Do not treat this as part of the page download or export flow.

AFTER CALLING:
- Use the returned projectDescription directly.
- Empty string means the project currently has no saved description.`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: projectDescriptionOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_description_upsert
// ============================================================

export const toolProjectDescriptionUpsert = {
  title: "PrimeUI Project Description Upsert",
  description: `Atomic external API helper. Creates, updates, or clears the shared project description for the scoped PrimeUI project.

WHEN TO USE:
- Use this only when the caller explicitly wants to change the shared project description.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- Whitespace-only input is valid and clears the saved description.

AFTER CALLING:
- Treat the returned projectDescription as the source of truth after persistence.`,
  inputSchema: projectDescriptionUpsertInputSchema,
  outputSchema: projectDescriptionOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_pages_list
// ============================================================

export const toolProjectPagesList = {
  title: "PrimeUI Project Pages List",
  description: `Atomic external API helper. Returns the current page inventory with page-level readiness and lightweight variant summaries.

WHEN TO USE:
- Use this when the caller needs page inventory from the external API itself.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- Do not replace get_project_info with this tool when the goal is the standard import/export flow; get_project_info remains the primary entry point there.

AFTER CALLING:
- Use the returned page resources as-is.
- If the caller needs one specific page by ID, use project_page_get.`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: projectApiPagesOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_page_get
// ============================================================

export const toolProjectPageGet = {
  title: "PrimeUI Project Page Get",
  description: `Atomic external API helper. Returns one page by pageId using the same shape as project_pages_list.

WHEN TO USE:
- Use this when the caller already knows the target pageId.
- This is an ADDITIONAL capability outside the primary import/export workflow.

AFTER CALLING:
- The response stays endpoint-aligned and returns a pages array envelope from the external API.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    pageId: z
      .string()
      .describe("PrimeUI page identifier to fetch from the external API."),
  },
  outputSchema: projectApiPagesOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_page_create
// ============================================================

export const toolProjectPageCreate = {
  title: "PrimeUI Project Page Create",
  description: `Atomic external API helper. Creates a new page and its initial variant.

WHEN TO USE:
- Use this only when the caller explicitly wants to create a page through the external API.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- Do not encode follow-up chains here. Creating more variants later is a possible next step, not a requirement.

AFTER CALLING:
- The returned pages payload reflects the immediate API result for the created page.
- If contentMode is ai, the initial variant may still be generating.`,
  inputSchema: projectPageCreateBaseInputSchema,
  outputSchema: projectApiPagesOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_page_variants_list
// ============================================================

export const toolProjectPageVariantsList = {
  title: "PrimeUI Project Page Variants List",
  description: `Atomic external API helper. Returns lightweight variant summaries for one page.

WHEN TO USE:
- Use this when the caller needs the variant inventory for an existing pageId.
- This is an ADDITIONAL capability outside the primary import/export workflow.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    pageId: z
      .string()
      .describe("PrimeUI page identifier whose variants should be listed."),
  },
  outputSchema: projectApiVariantsOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_page_variant_get
// ============================================================

export const toolProjectPageVariantGet = {
  title: "PrimeUI Project Page Variant Get",
  description: `Atomic external API helper. Returns one variant with its component payload.

WHEN TO USE:
- Use this when the caller already knows both pageId and variantId.
- This is an ADDITIONAL capability outside the primary import/export workflow.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    pageId: z
      .string()
      .describe("PrimeUI page identifier that owns the variant."),
    variantId: z.string().describe("PrimeUI variant identifier to fetch."),
  },
  outputSchema: projectApiVariantOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_page_variant_create
// ============================================================

export const toolProjectPageVariantCreate = {
  title: "PrimeUI Project Page Variant Create",
  description: `Atomic external API helper. Creates one new variant for an existing page.

WHEN TO USE:
- Use this only when the caller explicitly wants a new variant on an existing page.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- The new variant is created independently. Becoming active is a separate decision handled by project_page_active_variant_set.

AFTER CALLING:
- Treat the returned variant payload as the immediate API result.
- The create endpoint returns a lightweight variant summary only, not the full component payload.
- If contentMode is ai, the variant may still be generating.`,
  inputSchema: projectPageVariantCreateBaseInputSchema,
  outputSchema: projectApiVariantSummaryResultSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_page_active_variant_set
// ============================================================

export const toolProjectPageActiveVariantSet = {
  title: "PrimeUI Project Page Active Variant Set",
  description: `Atomic external API helper. Sets the export-selected active variant for one page.

WHEN TO USE:
- Use this only when the caller explicitly wants to switch the active variant.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- This tool performs exactly one operation: set the active variant. It does not create variants and does not export anything.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    pageId: z
      .string()
      .describe("PrimeUI page identifier whose active variant should change."),
    variantId: z
      .string()
      .describe("Variant identifier that should become active for export."),
  },
  outputSchema: projectApiPagesOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: component_candidates_get
// ============================================================

export const toolComponentCandidatesGet = {
  title: "PrimeUI Component Candidates Get",
  description: `External planning helper. Returns a ranked rich candidate shortlist for adding, inserting, or replacing one PrimeUI component in an exported-project page composition.

WHEN TO USE:
- Use this when building or editing a local exported-project page and you need PrimeUI scoring guidance for which componentId to choose next.
- The normal flow is append: pass the current ordered blocks and omit operation, which defaults to { "type": "append" }.
- Use insert or replace only when the caller is intentionally editing a specific position in the ordered blocks list.
- Use the returned copyHints.deliveryToolchain when the chosen candidate must be delivered locally: create_component_export, download_component_export, then copy_registry_component.

AFTER CALLING:
- Choose from candidates using uxScore, insertionScore, followingScore, group/family/layout rhythm, descriptions, defaults, and schemas.
- Do not mutate Prime page state from this tool result. This is planning guidance only.
- Keep the same pageSlug across later planning and validation calls so PrimeUI diagnostics stay correlated.`,
  inputSchema: componentCandidatesGetInputSchema,
  outputSchema: componentCandidatesGetOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: component_props_validate
// ============================================================

export const toolComponentPropsValidate = {
  title: "PrimeUI Component Props Validate",
  description: `External planning helper. Validates agent-authored props for one selected PrimeUI component before editing local exported-project files.

WHEN TO USE:
- Use this after choosing a component candidate and drafting props from the user's brief, candidate metadata, defaultProps, compactSchema, or jsonSchema.
- Keep pageSlug stable across candidate retrieval and validation so PrimeUI diagnostics stay correlated.
- Treat valid: false as actionable feedback, not a transport failure. Fix the props from errors and hints, then call this tool again.

AFTER CALLING:
- If valid is true, pass propsValidationId to create_component_export so copy_registry_component can return materialized insertion artifacts.
- If valid is false, correct props using errors[].path, errors[].message, and hints before editing local files.
- Do not mutate Prime page state from this tool result. This is validation guidance only.`,
  inputSchema: componentPropsValidateInputSchema,
  outputSchema: componentPropsValidateOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: project_issue_report_submit
// ============================================================

export const toolProjectIssueReportSubmit = {
  title: "PrimeUI Project Issue Report Submit",
  description: `Atomic external API helper. Submits an issue report for the scoped PrimeUI project.

WHEN TO USE:
- Use this when the caller wants to send a focused issue report through the external API.
- This is an ADDITIONAL capability outside the primary import/export workflow.
- This tool is independent from export, page, and variant flows.

AFTER CALLING:
- Use ticketId for user-facing confirmation or follow-up references.`,
  inputSchema: projectIssueReportSubmitInputSchema,
  outputSchema: issueReportSubmitOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: search_overview_get
// ============================================================

export const toolSearchOverviewGet = {
  title: "PrimeUI Search Overview Get",
  description: `Standalone managed-search helper. Returns the current search overview for the scoped PrimeUI project using the existing environment-first API contract.

WHEN TO USE:
- Use this as the starting point for MCP search work.
- This tool is intentionally separate from the page/export workflow chain.
- Call this before focused environment inspection or environment mutations when the current search state is not already known.

AFTER CALLING:
- Use runtimes as the authoritative inventory of search environments.
- Use search_environment_get when the caller wants one environment-focused view instead of the full overview payload.`,
  inputSchema: optionalProjectRootInputObjectSchema,
  outputSchema: searchOverviewOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: search_environment_get
// ============================================================

export const toolSearchEnvironmentGet = {
  title: "PrimeUI Search Environment Get",
  description: `Standalone managed-search helper. Returns one environment-focused view derived from the existing search overview contract.

WHEN TO USE:
- Use this when the caller already knows the target environment slug.
- This tool does not rely on a separate runtime-details endpoint; it is an MCP convenience projection over the overview API.

AFTER CALLING:
- Treat environment as the source of truth for the selected search environment.
- Treat relatedIndexes, recentJobs, and recentSyncRuns as MCP-filtered convenience data for this one environment.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    environmentSlug: searchEnvironmentSlugSchema,
  },
  outputSchema: searchEnvironmentOutputSchema,
  annotations: {
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: search_environment_create
// ============================================================

export const toolSearchEnvironmentCreate = {
  title: "PrimeUI Search Environment Create",
  description: `Standalone managed-search helper. Creates one new search environment through the existing PrimeUI search-environment management API.

WHEN TO USE:
- Use this only when the caller explicitly wants to create a new search environment.
- This is a power-user environment management tool, not part of the primary page/export workflow.

AFTER CALLING:
- Treat the returned environment payload as the immediate API result for the new environment.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    slug: searchEnvironmentSlugSchema,
    name: z.string().describe("Human-readable environment name to create."),
  },
  outputSchema: searchEnvironmentMutationOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: search_environment_update
// ============================================================

export const toolSearchEnvironmentUpdate = {
  title: "PrimeUI Search Environment Update",
  description: `Standalone managed-search helper. Updates one existing search environment through the PrimeUI search-environment management API.

WHEN TO USE:
- Use this only when the caller explicitly wants to update an existing search environment.
- Keep this narrow: name and status updates only.

AFTER CALLING:
- Treat the returned environment payload as the immediate API result for the updated environment.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    environmentSlug: searchEnvironmentSlugSchema,
    name: z
      .string()
      .optional()
      .describe("Optional new human-readable environment name."),
    status: searchRuntimeStatusSchema
      .optional()
      .describe("Optional new environment lifecycle status."),
  },
  outputSchema: searchEnvironmentMutationOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: search_environment_delete
// ============================================================

export const toolSearchEnvironmentDelete = {
  title: "PrimeUI Search Environment Delete",
  description: `Standalone managed-search helper. Deletes one search environment through the PrimeUI search-environment management API when the backend allows it.

WHEN TO USE:
- Use this only when the caller explicitly wants to delete an existing environment.
- This tool relies on normal API allow/deny behavior instead of inventing separate MCP policy summaries.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    environmentSlug: searchEnvironmentSlugSchema,
  },
  outputSchema: searchEnvironmentDeleteOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: true,
    idempotentHint: false,
    openWorldHint: true,
  },
};

// ============================================================
// Tool: search_environment_sync
// ============================================================

export const toolSearchEnvironmentSync = {
  title: "PrimeUI Search Environment Sync",
  description: `Standalone managed-search helper. Returns the real search sync command context for one environment and may execute it only when MCP can do so truthfully.

WHEN TO USE:
- Use this when the caller wants to sync one search environment against the actual local exported-project flow.
- This tool intentionally avoids pretending that a runtime-action API wrapper would always represent fresh local project state.

AFTER CALLING:
- If mode is guidance, run the returned command from workingDirectory next.
- If mode is executed, treat jobId and status as the immediate sync invocation result.`,
  inputSchema: {
    projectRoot: projectRootInputSchema,
    environmentSlug: searchEnvironmentSlugSchema,
  },
  outputSchema: searchEnvironmentSyncOutputSchema,
  annotations: {
    readOnlyHint: false,
    destructiveHint: false,
    idempotentHint: false,
    openWorldHint: true,
  },
};
